<template>
  <v-card>
    <v-layout class="app">
      <LateralMenuVue v-if="!isAllowAnonymous" />
      <AppBarVue v-if="showAppBar" />
      <MessageCenterEntryPoint v-if="!isAllowAnonymous && !isReadOnlyUser" />
      <v-main>
        <v-container fluid class="main-container">
          <router-view />
        </v-container>
      </v-main>
      <router-view name="EmbeddedKiosk" class="embedded-kiosk" />
    </v-layout>
  </v-card>
</template>
<script setup lang="ts">
import LateralMenuVue from '@/Core.UI.Domain/Components/LateralMenu.vue';
import AppBarVue from '@/Core.UI.Domain/Components/AppBar.vue';

import MessageCenterEntryPoint from '@/Core.Service.Domain/MessageCenter/MessageCenter.Common/Components/MessageCenterEntryPoint.vue';

import { watch, computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { checkAppVersion } from '@/utils/version-check';
import { isVersionCheckDisabled } from '@/configuration/featureFlags';

import { ToastService } from '@/plugins/toasts';

import { useIsMobile } from '@/Core.Patterns/Composables';

import { useStore } from 'vuex';
const store = useStore();

const toast = inject<ToastService>('toast')!;
// DISABLE_VERSION_CHECK (local only): the dev server has no version.json, so
// the poll would log an error every five minutes.
if (!isVersionCheckDisabled()) {
  setInterval(async () => {
    const hasUpdate = await checkAppVersion();
    if (hasUpdate) {
      toast.show({
        title: 'New Content Available',
        message: `New content is available. Please refresh the page.`,
        status: 'success',
        showButtons: true,
        buttonOptions: {
          text: 'Refresh',
          action: () => {
            const url = window.location.href.split('?')[0];
            const bustUrl = `${url}?cacheBust=${Date.now()}`;

            // Navigate with cache-busting query to force full reload
            window.location.replace(bustUrl);
          },
        },
        persistent: true,
      });
    }
  }, 60 * 5000); // every 5 min
}

const route = useRoute();

const isAllowAnonymous = computed(() => route.meta.allowAnonymous);

const showAppBar = computed(() => route.meta.showAppBar ?? true);

const isReadOnlyUser = computed(() => store.getters['session/isReadOnlyUser']);

watch(
  () => route,
  () => {
    localStorage.removeItem('controlAnchorPosition');
  }
);

const isMobile = useIsMobile();

watch(
  isMobile,
  (newVal) => {
    if (!newVal) {
      store.dispatch('UIStore/setLateraMenuOpen', false);
    } else {
      store.dispatch('UIStore/setLateraMenuOpen', true);
    }
  },
  { immediate: true }
);
</script>
<style lang="scss">
.app {
  background-color: $aliceBlue !important;
}

.main-container {
  min-height: 100vh !important;
  width: 98%;
  box-sizing: border-box;
}

.embedded-kiosk {
  position: absolute;
  height: 100vh; /* Ensures full viewport height */
  width: 100vw; /* Ensures full viewport width */
  background-color: #e5e5e5;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
